import React, { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useHistory, useLocation } from 'react-router-dom';
import {
  Button,
  FilterLabel,
  FilterReset,
  LoadingSpinner,
  PagedTable,
  SelectOption,
  SortEvent,
  Th,
  ThSortable,
  formatDatetime,
  useBoundForm
} from 'common.ui';
import { auditEventsClient } from 'api/AdgangClients';
import { AuditEventSearchOrderBy, AuditEventSource, SortOrder } from 'api/adgang';
import Page from 'components/page/Page';
import RoutePaths from 'RoutePaths';
import { Col, Container, Row, Tab, Tabs } from 'react-bootstrap';
import AuditLogSummary from './AuditLogSummary';
import AuditLogGeography from './AuditLogGeography';
import AuditLogSecurity from './AuditLogSecurity';
import './AuditLogs.scss';

const PAGE_SIZE = 25;
const DEFAULT_SORT_FIELD = AuditEventSearchOrderBy.EventDateTime;
const DEFAULT_SORT_DIR = 'desc';

// Filterfelt som ligger i skjemaet (bindes via useBoundForm). Datofeltene styres separat
// fordi common.ui ikke har en dato-komponent.
type SearchForm = {
  source: string;
  activity: string;
  category: string;
  initiatedByUpn: string;
  targetUpn: string;
  ipAddress: string;
  result: string;
};

type UrlSearch = SearchForm & {
  fromUtc: string;
  toUtc: string;
  page: string;
  sortField: string;
  sortDir: string;
};

const URL_DEFAULTS: UrlSearch = {
  source: '',
  activity: '',
  category: '',
  initiatedByUpn: '',
  targetUpn: '',
  ipAddress: '',
  result: '',
  fromUtc: '',
  toUtc: '',
  page: '1',
  sortField: DEFAULT_SORT_FIELD,
  sortDir: DEFAULT_SORT_DIR
};

const sourceOptions: SelectOption[] = [
  { id: '', text: 'Alle kilder' },
  { id: AuditEventSource.DirectoryAudit as string, text: 'Katalogendring' },
  { id: AuditEventSource.SignIn as string, text: 'Innlogging' }
];

function sourceLabel(source: AuditEventSource | string | undefined): string {
  if (source === AuditEventSource.DirectoryAudit) return 'Katalogendring';
  if (source === AuditEventSource.SignIn) return 'Innlogging';
  return source ?? '';
}

// Enkel URL-basert filtertilstand slik at et revisjonssøk kan bokmerkes og deles.
function useUrlState<T extends Record<string, string>>(defaults: T) {
  const location = useLocation();
  const history = useHistory();

  const current = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const out: Record<string, string> = { ...defaults };
    for (const key of Object.keys(defaults)) {
      const v = params.get(key);
      if (v !== null) out[key] = v;
    }
    return out as T;
  }, [location.search]);

  const set = useCallback(
    (next: Partial<T>) => {
      const params = new URLSearchParams(location.search);
      for (const [k, v] of Object.entries(next)) {
        params.delete(k);
        if (v != null && v !== '') params.set(k, String(v));
      }
      history.replace({ ...location, search: params.toString() });
    },
    [location, history]
  );

  return [current, set] as const;
}

function hasFilters(s: UrlSearch): boolean {
  return !!(
    s.source ||
    s.activity ||
    s.category ||
    s.initiatedByUpn ||
    s.targetUpn ||
    s.ipAddress ||
    s.result ||
    s.fromUtc ||
    s.toUtc
  );
}

function nextDayMidnight(isoDate: string): Date {
  const d = new Date(`${isoDate}T00:00:00`);
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + 1);
}

// Bygger filterargumentene som deles av tabell- og oppsummeringskallet.
function toFilterArgs(s: UrlSearch) {
  return {
    source: s.source ? (s.source as AuditEventSource) : undefined,
    category: s.category || undefined,
    activity: s.activity || undefined,
    initiatedByUpn: s.initiatedByUpn || undefined,
    targetUpn: s.targetUpn || undefined,
    ipAddress: s.ipAddress || undefined,
    result: s.result || undefined,
    fromUtc: s.fromUtc ? new Date(`${s.fromUtc}T00:00:00`) : undefined,
    // Til-og-med hele dagen: midnatt dagen etter, brukt som eksklusiv øvre grense i backend.
    toUtc: s.toUtc ? nextDayMidnight(s.toUtc) : undefined
  };
}

function AuditLogs() {
  const [search, setSearch] = useUrlState<UrlSearch>(URL_DEFAULTS);
  const [formValues, setFormValues] = useState<SearchForm>(() => ({
    source: search.source,
    activity: search.activity,
    category: search.category,
    initiatedByUpn: search.initiatedByUpn,
    targetUpn: search.targetUpn,
    ipAddress: search.ipAddress,
    result: search.result
  }));
  const [formKey, setFormKey] = useState(0);

  const currentPage = Number(search.page) || 1;
  const sorting = {
    field: (search.sortField || DEFAULT_SORT_FIELD) as AuditEventSearchOrderBy,
    direction: (search.sortDir || DEFAULT_SORT_DIR) as 'asc' | 'desc'
  };
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const filterArgs = toFilterArgs(search);

  const { data, isLoading } = useQuery({
    queryKey: ['auditEvents', search],
    queryFn: () =>
      auditEventsClient.apiAuditEventsGet({
        pageNumber: currentPage,
        pageSize: PAGE_SIZE,
        sortOrder: sorting.direction === 'asc' ? SortOrder.Ascending : SortOrder.Descending,
        orderField: sorting.field,
        ...filterArgs
      }),
    keepPreviousData: true,
    staleTime: 30_000
  });

  const { data: summary } = useQuery({
    queryKey: ['auditSummary', filterArgs],
    queryFn: () => auditEventsClient.apiAuditEventsSummaryGet(filterArgs),
    staleTime: 30_000
  });

  const { form, FormContainer, Dropdown } = useBoundForm<SearchForm>({
    onSubmit: async (values) => {
      setFormValues(values);
      setSearch({ ...values, page: '1' });
    },
    model: formValues
  });

  function applyFilterRemoval(update: Partial<UrlSearch>) {
    const next = { ...formValues, ...update } as SearchForm;
    setFormValues(next);
    setFormKey((k) => k + 1);
    setSearch({ ...update, page: '1' });
  }

  function resetAll() {
    const empty: SearchForm = {
      source: '',
      activity: '',
      category: '',
      initiatedByUpn: '',
      targetUpn: '',
      ipAddress: '',
      result: ''
    };
    setFormValues(empty);
    setFormKey((k) => k + 1);
    setSearch({ ...URL_DEFAULTS, sortField: sorting.field, sortDir: sorting.direction });
  }

  const handleSort = (e: SortEvent<AuditEventSearchOrderBy>) => {
    setSearch({ sortField: e.field, sortDir: e.direction, page: '1' });
  };

  const totalHits = data?.pageInfo?.totalCount ?? 0;

  return (
    <Page header='Revisjonslogg' title='Revisjonslogg'>
      <Container fluid className='audit-logs'>
        <FormContainer key={formKey} form={form}>
          <Row className='filter-row'>
            <Col sm={12} lg={3}>
              <Dropdown form={form} name='source' label='Kilde' options={sourceOptions} />
            </Col>
            {/* Midlertidig skjult - kun kilde og datofilter er aktive foreløpig.
            <Col sm={12} lg={3}>
              <Input
                form={form}
                name='activity'
                label='Aktivitet'
                placeholder='Aktivitet'
                onEnterPressed={(e) => {
                  const next = { ...formValues, activity: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={3}>
              <Input
                form={form}
                name='category'
                label='Kategori'
                placeholder='Kategori'
                onEnterPressed={(e) => {
                  const next = { ...formValues, category: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={3}>
              <Input
                form={form}
                name='result'
                label='Resultat'
                placeholder='Resultat'
                onEnterPressed={(e) => {
                  const next = { ...formValues, result: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
            */}
          {/* Midlertidig skjult - UPN og IP-filter.
          <Row>
            <Col sm={12} lg={3}>
              <Input
                form={form}
                name='initiatedByUpn'
                label='Utført av (UPN)'
                placeholder='navn@domene.no'
                onEnterPressed={(e) => {
                  const next = { ...formValues, initiatedByUpn: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={3}>
              <Input
                form={form}
                name='targetUpn'
                label='Mål (UPN)'
                placeholder='navn@domene.no'
                onEnterPressed={(e) => {
                  const next = { ...formValues, targetUpn: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
            <Col sm={12} lg={3}>
              <Input
                form={form}
                name='ipAddress'
                label='IP-adresse'
                placeholder='IP-adresse'
                onEnterPressed={(e) => {
                  const next = { ...formValues, ipAddress: e };
                  setFormValues(next);
                  setSearch({ ...next, page: '1' });
                }}
              />
            </Col>
          </Row>
          */}
            <Col sm={6} lg={3}>
              <label className='form-label' htmlFor='auditFromUtc'>
                Fra dato
              </label>
              <input
                id='auditFromUtc'
                type='date'
                className='form-control'
                value={search.fromUtc}
                max={search.toUtc || undefined}
                onChange={(e) => setSearch({ fromUtc: e.target.value, page: '1' })}
              />
            </Col>
            <Col sm={6} lg={3}>
              <label className='form-label' htmlFor='auditToUtc'>
                Til dato
              </label>
              <input
                id='auditToUtc'
                type='date'
                className='form-control'
                value={search.toUtc}
                min={search.fromUtc || undefined}
                onChange={(e) => setSearch({ toUtc: e.target.value, page: '1' })}
              />
            </Col>
            <Col className='d-flex flex-column align-items-end' sm={12} lg={3}>
              {/* Usynlig etikett gir samme høyde som de andre feltene, så knappen havner i input-båndet. */}
              <span className='form-label' aria-hidden='true' style={{ visibility: 'hidden' }}>
                Søk
              </span>
              <Button type='submit' text='Søk' />
            </Col>
          </Row>
          <Row className='pb-3'>
            <Col sm={12}>
              <br />
              {search.source && (
                <FilterLabel
                  key='source'
                  label={sourceLabel(search.source)}
                  onRemove={() => applyFilterRemoval({ source: '' })}
                />
              )}
              {/* Midlertidig skjult - filterchips for de deaktiverte filtrene.
              {search.activity && (
                <FilterLabel
                  key='activity'
                  label={search.activity}
                  onRemove={() => applyFilterRemoval({ activity: '' })}
                />
              )}
              {search.category && (
                <FilterLabel
                  key='category'
                  label={search.category}
                  onRemove={() => applyFilterRemoval({ category: '' })}
                />
              )}
              {search.result && (
                <FilterLabel
                  key='result'
                  label={search.result}
                  onRemove={() => applyFilterRemoval({ result: '' })}
                />
              )}
              {search.initiatedByUpn && (
                <FilterLabel
                  key='initiatedByUpn'
                  label={search.initiatedByUpn}
                  onRemove={() => applyFilterRemoval({ initiatedByUpn: '' })}
                />
              )}
              {search.targetUpn && (
                <FilterLabel
                  key='targetUpn'
                  label={search.targetUpn}
                  onRemove={() => applyFilterRemoval({ targetUpn: '' })}
                />
              )}
              {search.ipAddress && (
                <FilterLabel
                  key='ipAddress'
                  label={search.ipAddress}
                  onRemove={() => applyFilterRemoval({ ipAddress: '' })}
                />
              )}
              */}
              {search.fromUtc && (
                <FilterLabel
                  key='fromUtc'
                  label={`Fra ${search.fromUtc}`}
                  onRemove={() => setSearch({ fromUtc: '', page: '1' })}
                />
              )}
              {search.toUtc && (
                <FilterLabel
                  key='toUtc'
                  label={`Til ${search.toUtc}`}
                  onRemove={() => setSearch({ toUtc: '', page: '1' })}
                />
              )}
              {hasFilters(search) && <FilterReset key='reset' label='Fjern alle' onRemove={resetAll} />}
            </Col>
          </Row>
        </FormContainer>

        <Tabs id='audit-log-tabs' defaultActiveKey='oversikt' className='mb-3' mountOnEnter unmountOnExit>
          <Tab eventKey='oversikt' title='Oversikt'>
            <AuditLogSummary summary={summary} />
          </Tab>
          <Tab eventKey='geografi' title='Geografi'>
            <AuditLogGeography perCountry={summary?.perCountry ?? undefined} />
          </Tab>
          <Tab eventKey='sikkerhet' title='Sikkerhet'>
            <AuditLogSecurity
              perIp={summary?.perIp ?? undefined}
              perInitiator={summary?.perInitiator ?? undefined}
              perHour={summary?.perHour ?? undefined}
            />
          </Tab>
          <Tab eventKey='hendelser' title='Hendelser'>
            {isLoading ? (
              <LoadingSpinner />
            ) : (
              <PagedTable
                startIndex={startIndex}
                totalHits={totalHits}
                pageSize={PAGE_SIZE}
                onNextClick={() => setSearch({ page: String(currentPage + 1) })}
                onPreviousClick={() => setSearch({ page: String(currentPage - 1) })}
              >
            <thead>
              <tr>
                <ThSortable
                  title='Tidspunkt'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={AuditEventSearchOrderBy.EventDateTime}
                />
                <ThSortable
                  title='Kilde'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={AuditEventSearchOrderBy.Source}
                />
                <Th title='Kategori' />
                <ThSortable
                  title='Aktivitet'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={AuditEventSearchOrderBy.Activity}
                />
                <Th title='Resultat' />
                <ThSortable
                  title='Utført av'
                  currentSort={sorting}
                  onSort={handleSort}
                  field={AuditEventSearchOrderBy.InitiatedByUpn}
                />
                <Th title='IP-adresse' />
              </tr>
            </thead>
            <tbody>
              {data?.data?.map((e) => (
                <tr key={String(e.auditEventId)}>
                  <td className='text-nowrap'>
                    <Link to={`${RoutePaths.auditlogs}/${e.auditEventId}`}>
                      {e.eventDateTime && formatDatetime(e.eventDateTime, true)}
                    </Link>
                  </td>
                  <td>{sourceLabel(e.source)}</td>
                  <td>{e.category}</td>
                  <td>{e.activity}</td>
                  <td>{e.result}</td>
                  <td>{e.initiatedByDisplayName || e.initiatedByUpn}</td>
                  <td>{e.ipAddress}</td>
                </tr>
              ))}
            </tbody>
              </PagedTable>
            )}
          </Tab>
        </Tabs>
      </Container>
    </Page>
  );
}

export default AuditLogs;
